import React, { useState, useEffect, useRef, useCallback } from 'react';
import Modal from 'react-bootstrap/Modal';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../styles/SectionTracking.scss';
import { useTranslation } from 'react-i18next';
import { getToken } from './sessionManager';
import useUserSession from './useUserSession';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const EVENT_LABELS = {
  GTOT: { en: 'Gate Out', fr: 'Sortie de terminal', icon: '↗' },
  GTIN: { en: 'Gate In', fr: 'Entrée au terminal', icon: '↘' },
  LOAD: { en: 'Loaded on vessel', fr: 'Chargé sur navire', icon: '⛴' },
  DISC: { en: 'Discharged', fr: 'Déchargé', icon: '⚓' },
  ARRI: { en: 'Arrival', fr: 'Arrivée', icon: '📍' },
  DEPA: { en: 'Departure', fr: 'Départ', icon: '🚢' },
  TRSH: { en: 'Transshipment', fr: 'Transbordement', icon: '🔁' },
};

function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions.map(p => [p.latitude, p.longitude]));
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [positions, map]);
  return null;
}

function SectionTracking({ show, onHide, initialBookingRef }) {
  const { t, i18n } = useTranslation();
  const { user } = useUserSession();
  const [blNumber, setBlNumber] = useState('');
  const [activeBookingRef, setActiveBookingRef] = useState('');
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const inputRef = useRef(null);
  const isFr = i18n.language === 'fr';

  const token = user?.token || getToken();

  const subscribe = useCallback(async (ref) => {
    if (!ref || !token) return;
    setStatus('loading');
    setErrorMsg('');
    setEvents([]);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tracking/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ carrierBookingReference: ref }),
      });
      if (!response.ok) {
        setStatus('error');
        setErrorMsg(t('tracking.error'));
        return;
      }
      const data = await response.json();
      if (data.status === 0 || data.status === 2) {
        setActiveBookingRef(ref);
        setEvents(data.events || []);
        if (data.subscription?.status) setSubscriptionStatus(data.subscription.status);
        setStatus((data.events || []).length === 0 ? 'empty' : 'success');
      } else {
        setStatus('error');
        setErrorMsg(data.message || t('tracking.error'));
      }
    } catch {
      setStatus('error');
      setErrorMsg(t('tracking.error'));
    }
  }, [token, t]);

  const refresh = useCallback(async () => {
    if (!activeBookingRef || !token) return;
    setRefreshing(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tracking/position`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ carrierBookingReference: activeBookingRef }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.status === 0) {
          if (data.events) setEvents(data.events);
          if (data.subscription?.status) setSubscriptionStatus(data.subscription.status);
          if ((data.events || []).length === 0) setStatus('empty'); else setStatus('success');
        }
      }
    } catch {}
    setRefreshing(false);
  }, [activeBookingRef, token]);

  useEffect(() => {
    if (!show) {
      setEvents([]);
      setStatus('idle');
      setErrorMsg('');
      setBlNumber('');
      setActiveBookingRef('');
      setSubscriptionStatus(null);
      return;
    }
    if (initialBookingRef && token) {
      setBlNumber(initialBookingRef);
      subscribe(initialBookingRef);
    } else if (inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [show, initialBookingRef, token, subscribe]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!blNumber.trim()) return;
    if (!token) {
      setStatus('error');
      setErrorMsg(t('tracking.loginRequired'));
      return;
    }
    subscribe(blNumber.trim());
  };

  const geoEvents = events.filter(e => e.latitude != null && e.longitude != null);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString(isFr ? 'fr-FR' : 'en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const eventLabel = (ev) => {
    if (ev.eventDescription) return ev.eventDescription;
    const meta = EVENT_LABELS[ev.eventType];
    if (meta) return isFr ? meta.fr : meta.en;
    return ev.eventType || '-';
  };

  const eventIcon = (ev) => EVENT_LABELS[ev.eventType]?.icon || '•';

  const subStatusLabel = () => {
    if (subscriptionStatus === 'active') return isFr ? 'Abonnement actif' : 'Subscription active';
    if (subscriptionStatus === 'pending') return isFr ? 'En attente de données…' : 'Waiting for data…';
    if (subscriptionStatus === 'error') return isFr ? "Erreur d'abonnement" : 'Subscription error';
    return null;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="SectionTracking">
      <Modal.Header closeButton>
        <Modal.Title>
          {t('tracking.title')}
          {activeBookingRef && <span className='SectionTracking_ref'> — {activeBookingRef}</span>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form className="SectionTracking_search" onSubmit={handleSearch}>
          <input
            ref={inputRef}
            type="text"
            className="form-control"
            placeholder={t('tracking.placeholder')}
            value={blNumber}
            onChange={(e) => setBlNumber(e.target.value)}
          />
          <button type="submit" className="btn" disabled={status === 'loading' || !blNumber.trim()}>
            {status === 'loading' ? (
              <span className="spinner-border spinner-border-sm" role="status"></span>
            ) : (
              t('tracking.search')
            )}
          </button>
          {activeBookingRef && (
            <button type="button" className="btn SectionTracking_refresh" onClick={refresh} disabled={refreshing} title={isFr ? 'Actualiser' : 'Refresh'}>
              <span className={refreshing ? 'spin' : ''}>↻</span>
            </button>
          )}
        </form>

        {subStatusLabel() && (
          <div className={`SectionTracking_subStatus SectionTracking_subStatus--${subscriptionStatus}`}>
            {subStatusLabel()}
          </div>
        )}

        {status === 'error' && (
          <div className="SectionTracking_error">{errorMsg}</div>
        )}

        {status === 'empty' && (
          <div className="SectionTracking_empty">{t('tracking.noResults')}</div>
        )}

        {!token && status === 'idle' && (
          <div className="SectionTracking_info">{t('tracking.loginRequired')}</div>
        )}

        <div className="SectionTracking_map">
          <MapContainer center={[20, 0]} zoom={2} style={{ width: '100%', height: '100%' }} scrollWheelZoom={true}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {geoEvents.map((event, index) => (
              <Marker key={index} position={[event.latitude, event.longitude]}>
                <Popup>
                  <strong>{eventLabel(event)}</strong><br />
                  {event.locationName && <>{event.locationName}<br /></>}
                  {event.vesselName && <><em>{event.vesselName}</em><br /></>}
                  {event.eventDateTime && <span>{formatDate(event.eventDateTime)}</span>}
                </Popup>
              </Marker>
            ))}
            {geoEvents.length > 0 && <FitBounds positions={geoEvents} />}
          </MapContainer>
        </div>

        {events.length > 0 && (
          <div className="SectionTracking_timeline">
            <h4>{events.length} {isFr ? 'événement(s)' : 'event(s)'}</h4>
            <ol className="SectionTracking_timeline_list">
              {events.map((event, index) => {
                const isLast = index === events.length - 1;
                return (
                  <li key={event._id || index} className={`SectionTracking_timeline_item ${isLast ? 'is-active' : ''}`}>
                    <span className="SectionTracking_timeline_dot">{eventIcon(event)}</span>
                    <div className="SectionTracking_timeline_card">
                      <div className="SectionTracking_timeline_card_title">{eventLabel(event)}</div>
                      {event.locationName && (
                        <div className="SectionTracking_timeline_card_row">
                          <span>📍</span>
                          <span>{event.locationName}{event.unLocationCode ? ` (${event.unLocationCode})` : ''}</span>
                        </div>
                      )}
                      {event.transportMode && (
                        <div className="SectionTracking_timeline_card_row">
                          <span>{event.transportMode === 'Maritime' ? '⛵' : event.transportMode === 'Rail' ? '🚆' : '🚚'}</span>
                          <span>{event.transportMode}</span>
                        </div>
                      )}
                      {event.vesselName && (
                        <div className="SectionTracking_timeline_card_row">
                          <span>🚢</span>
                          <em>{event.vesselName}</em>
                        </div>
                      )}
                      {event.equipmentReference && (
                        <div className="SectionTracking_timeline_card_row">
                          <span>📦</span>
                          <span>{event.equipmentReference}</span>
                        </div>
                      )}
                      <div className="SectionTracking_timeline_card_date">{formatDate(event.eventDateTime)}</div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default SectionTracking;
